import type { CmmdProject } from "@cmmd-forge/contracts";

import type { SidebarThreadSummary } from "~/types";
import { SettingsRow, SettingsSection } from "../settings/settingsLayout";
import { CmmdProjectDeleteControl } from "./CmmdProjectDeleteControl";
import type { ForgeProjectRemovalTarget } from "./CmmdProjectDeleteControl";
import { forgeExecutionUnavailableMessage } from "./cmmdProjectDangerGate";
import type { ProjectExecutionStatus } from "./projectExecutionStatus";

export interface CmmdProjectDangerSectionProps {
  readonly project: CmmdProject;
  readonly threadSummaries: readonly SidebarThreadSummary[];
  readonly executionStatus: ProjectExecutionStatus;
  readonly forgeRemovalTargets: readonly ForgeProjectRemovalTarget[];
  readonly onForgeProjectDeleted: () => Promise<void>;
  readonly onRefresh: () => Promise<void>;
}

/**
 * The project's one destructive action: delete the Forge project.
 *
 * A **Forge project** is a row in Forge's database plus its link to CMMD — the
 * thing that lets Forge check out a repo and spin up sprites. A **CMMD project**
 * is the planning board. Two objects, and each side deletes only the one it
 * owns: Forge deletes Forge projects, CMMD owns the board's lifecycle on its own
 * surface.
 *
 * So there is deliberately no "delete the CMMD project" here, and no archive
 * either — archiving is project management, and Forge has no `project.archive`
 * command at all (its surface is create / meta.update / delete). Reaching into
 * CMMD's lifecycle from this page is what this section used to do and no longer
 * does.
 */
export function CmmdProjectDangerSection(props: CmmdProjectDangerSectionProps) {
  return (
    <SettingsSection title="Danger">
      <SettingsRow
        title="Delete project"
        description="Deletes this project's threads, runtime and sprite. The CMMD project is kept."
        status={forgeExecutionUnavailableMessage({
          executionStatus: props.executionStatus,
          forgeTargetCount: props.forgeRemovalTargets.length,
        })}
        control={
          <CmmdProjectDeleteControl
            project={props.project}
            forgeTargets={props.forgeRemovalTargets}
            threadSummaries={props.threadSummaries}
            onForgeProjectDeleted={props.onForgeProjectDeleted}
            onRefresh={props.onRefresh}
          />
        }
      />
    </SettingsSection>
  );
}
